import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { RelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAM";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { SaveOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  List,
  Row,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useState } from "react";

interface FormValues {
  descricaoParecer: string;
  parecerFavoravel: boolean;
}

export function FormParecerRAM({
  item,
}: {
  item: RelatorioAM["itens"][number];
}) {
  const [form] = Form.useForm<FormValues>();
  const { message, notification } = App.useApp();
  const { getPlanoAcaoByItemIdApi, getAnexoPlanoAcaoApi, submitParecerApi } =
    useRelatorioAM();
  const [loading, setLoading] = useState(false);
  const parecerFavoravel = Form.useWatch("parecerFavoravel", form);

  const { data: ultimoPlanoAcao, refetch } = useQuery({
    queryKey: ["PLANO_ACAO_BY_ITEM_ID", item.id],
    enabled: !!item.id,
    queryFn: async () => {
      const response = await getPlanoAcaoByItemIdApi(item.id!);

      const ultimoPlano =
        response.length > 0 ? response[response.length - 1] : null;

      return ultimoPlano;
    },
  });

  const handleFinish = async (values: FormValues) => {
    if (!ultimoPlanoAcao) return;

    try {
      setLoading(true);
      message.loading({ content: "Salvando parecer...", key: "saving" });
      await submitParecerApi(ultimoPlanoAcao.id, {
        situacaoParecer: values.parecerFavoravel ? "FAVORAVEL" : "DESFAVORAVEL",
        descricaoParecer: values.descricaoParecer,
      });
      message.destroy("saving");
      setLoading(false);

      notification.success({
        message: "Parecer salvo com sucesso na base de dados.",
      });

      refetch();
    } catch (error) {
      setLoading(false);
      message.destroy("saving");
      const messageError = formatMessageError(error);
      console.error("Error submitting parecer:", error);
      notification.error({
        message: "Não foi possivel salvar parecer",
        description: messageError,
      });
    }
  };

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoPlanoAcaoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  return (
    <div>
      <Divider>
        <Typography.Title level={5}>Parecer Plano de Ação</Typography.Title>
      </Divider>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Solução Geral:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.solucaoGeral || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Ação Imediata:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoImediata
              ? dayjs(ultimoPlanoAcao.prazoAcaoImediata).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Medida Corretiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.medidaCorretiva || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Ação Corretiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoCorretiva
              ? dayjs(ultimoPlanoAcao.prazoAcaoCorretiva).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Medida Preventiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.medidaPreventiva || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Medida Preventiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoPreventiva
              ? dayjs(ultimoPlanoAcao.prazoAcaoPreventiva).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>

        {ultimoPlanoAcao?.anexos && ultimoPlanoAcao.anexos.length > 0 && (
          <Col xs={24}>
            <Typography.Text strong>Anexos:</Typography.Text>
            <List
              dataSource={ultimoPlanoAcao.anexos || []}
              renderItem={(anexo) => (
                <List.Item>
                  <Button
                    type="link"
                    onClick={() => handleOpenAnexo(anexo.nomeUpload)}
                  >
                    {anexo.nomeArquivo}
                  </Button>
                </List.Item>
              )}
            />
          </Col>
        )}
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{ marginTop: 16 }}
        initialValues={{ parecer: true }}
      >
        <Form.Item
          name="parecerFavoravel"
          valuePropName="checked"
          label="Parecer"
          tooltip="Marque se o parecer for favorável ao plano de ação."
        >
          <Checkbox>Favorável</Checkbox>
        </Form.Item>

        <Form.Item
          name="descricaoParecer"
          label="Descrição do Parecer"
          rules={[
            {
              required: !parecerFavoravel,
              message: "Informe a descrição do parecer.",
            },
          ]}
        >
          <Input.TextArea rows={2} placeholder="Descreva o parecer" />
        </Form.Item>

        <Row justify="center">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              disabled={!!ultimoPlanoAcao?.dataParecer}
            >
              Salvar Parecer
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
